import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentPaste as PasteIcon,
} from "@mui/icons-material";
import axios from "axios";

const TemplatePage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState({ title: "", body: "" });
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const API_BASE = import.meta.env.VITE_API_BASE;

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE}/get-templates`, {}, { withCredentials: true });
      if (response.data.success) {
        setTemplates(response.data.templates);
        setError("");
      } else {
        setError(response.data.message || "Failed to fetch templates");
      }
    } catch (err) {
      console.error(err);
      setError("An error occurred while fetching templates.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleOpenModal = (template = { title: "", body: "" }) => {
    setCurrentTemplate(template);
    setIsEditing(!!template.id);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentTemplate({ title: "", body: "" });
  };

  const handleSave = async () => {
    if (!currentTemplate.title || !currentTemplate.body) {
      alert("Please fill in both title and body.");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API_BASE}/save-template`, currentTemplate, { withCredentials: true });
      if (response.data.success) {
        fetchTemplates();
        handleCloseModal();
      } else {
        alert(response.data.message || "Failed to save template");
      }
    } catch (err) {
      console.error(err);
      alert("An error occurred while saving the template.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this template?")) {
      try {
        const response = await axios.post(`${API_BASE}/delete-template`, { id }, { withCredentials: true });
        if (response.data.success) {
          fetchTemplates();
        } else {
          alert(response.data.message || "Failed to delete template");
        }
      } catch (err) {
        console.error(err);
        alert("An error occurred while deleting the template.");
      }
    }
  };

  const insertPlaceholder = (placeholder) => {
    setCurrentTemplate({
      ...currentTemplate,
      body: currentTemplate.body + `{{${placeholder}}}`,
    });
  };

  const userPlaceholders = [
    { label: "My Name", value: "name" },
    { label: "My Company", value: "company" },
    { label: "My Role", value: "role" },
    { label: "My Email", value: "email" },
    { label: "My Phone", value: "phone" },
    { label: "My Website", value: "website" },
  ];

  const clientPlaceholders = [
    { label: "Client Name", value: "client_name" },
    { label: "Client Company", value: "client_company" },
    { label: "Client Title", value: "client_title" },
    { label: "Client Email", value: "client_email" },
    { label: "Client Phone", value: "client_phone" },
    { label: "Client Website", value: "client_website" },
  ];

  if (loading && templates.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
        <Typography variant="h4" fontWeight="bold">
          Message Templates
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenModal()}
          sx={{ borderRadius: 2 }}
        >
          Add Template
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {templates.length === 0 && !loading ? (
        <Box sx={{ textAlign: "center", mt: 8, p: 4, bgcolor: "background.paper", borderRadius: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No templates yet. Click "Add Template" to create one.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={3}>
          {templates.map((template) => (
            <Card key={template.id} elevation={2} sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {template.title}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    whiteSpace: "pre-wrap",
                    bgcolor: "#f9fafb",
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid #e5e7eb",
                  }}
                >
                  {template.body}
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: "flex-end", px: 2, pb: 2 }}>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => handleOpenModal(template)}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleDelete(template.id)}
                >
                  Delete
                </Button>
              </CardActions>
            </Card>
          ))}
        </Stack>
      )}

      <Dialog open={isModalOpen} onClose={handleCloseModal} fullWidth maxWidth="md">
        <DialogTitle>{isEditing ? "Edit Template" : "Add New Template"}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Template Title"
              fullWidth
              value={currentTemplate.title}
              onChange={(e) => setCurrentTemplate({ ...currentTemplate, title: e.target.value })}
              placeholder="e.g., Post-Meeting Follow-up"
            />
            <Box>
              <Typography variant="subtitle2" gutterBottom color="text.secondary" fontWeight="bold">
                Client Placeholders:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                {clientPlaceholders.map((p) => (
                  <Chip
                    key={p.value}
                    label={`{{${p.value}}}`}
                    onClick={() => insertPlaceholder(p.value)}
                    icon={<PasteIcon style={{ fontSize: 16 }} />}
                    color="primary"
                    variant="outlined"
                    sx={{ mb: 1, cursor: "pointer", "&:hover": { backgroundColor: "rgba(25, 118, 210, 0.04)" } }}
                  />
                ))}
              </Stack>

              <Typography variant="subtitle2" gutterBottom color="text.secondary" fontWeight="bold">
                Your Placeholders:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {userPlaceholders.map((p) => (
                  <Chip
                    key={p.value}
                    label={`{{${p.value}}}`}
                    onClick={() => insertPlaceholder(p.value)}
                    icon={<PasteIcon style={{ fontSize: 16 }} />}
                    color="secondary"
                    variant="outlined"
                    sx={{ mb: 1, cursor: "pointer", "&:hover": { backgroundColor: "rgba(156, 39, 176, 0.04)" } }}
                  />
                ))}
              </Stack>
            </Box>
            <TextField
              label="Template Body"
              fullWidth
              multiline
              rows={8}
              value={currentTemplate.body}
              onChange={(e) => setCurrentTemplate({ ...currentTemplate, body: e.target.value })}
              placeholder="Write your template here. Use {{client_name}}, {{user_company}} etc. for dynamic data."
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseModal} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {isEditing ? "Update Template" : "Save Template"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TemplatePage;
